import { useCallback, useEffect } from 'react';

interface UseSmoothScrollOptions {
  offset?: number;
  duration?: number;
}

export function useSmoothScroll({ offset = 80 }: UseSmoothScrollOptions = {}) {
  const scrollToElement = useCallback((elementId: string) => {
    const element = document.getElementById(elementId);
    if (!element) return;

    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.scrollY - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  }, [offset]);

  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  // Handle hash links on page load
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (hash) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        scrollToElement(hash);
      }, 100);
    }
  }, [scrollToElement]);

  // Intercept hash link clicks for smoother scrolling
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href^="#"]') as HTMLAnchorElement;
      
      if (anchor && anchor.hash) {
        e.preventDefault();
        const id = anchor.hash.slice(1);
        
        if (id) {
          scrollToElement(id);
          // Update URL without triggering scroll
          window.history.pushState(null, '', anchor.hash);
        } else {
          scrollToTop();
        }
      }
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [scrollToElement, scrollToTop]);

  return { scrollToElement, scrollToTop };
}
